import type { ButtonHTMLAttributes } from 'react';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import {
  createReadableClass,
  getColorWithAlpha,
  getInvertedColor
} from '@elfsight/widget-utils';
import { useFiltersTheme } from '../../filters-theme-provider';

export type ApplyFiltersButtonProps =
  ButtonHTMLAttributes<HTMLButtonElement> & { count?: number };

export const ApplyFiltersButton = forwardRef<
  HTMLButtonElement,
  ApplyFiltersButtonProps
>(
  (
    { children, onClick, disabled, count, ...forwardingProps },
    forwardingRef
  ) => {
    const { accentColor, textColor } = useFiltersTheme();

    return (
      <Button
        {...forwardingProps}
        $accentColor={accentColor}
        $textColor={textColor}
        $disabled={disabled || count === 0}
        disabled={disabled || count === 0}
        onClick={onClick}
        ref={forwardingRef}
      >
        {children}
      </Button>
    );
  }
);

const Button = styled.button.attrs({
  className: createReadableClass({
    component: 'apply-filters-button',
    element: 'button'
  })
})<{
  $accentColor: string;
  $textColor: string;
  $disabled?: boolean;
}>`
  padding: 8px 16px;
  text-align: center;
  font-size: 14px;
  line-height: 18px;
  font-weight: 600;
  border-radius: 8px;
  border: 1px solid transparent;
  transition:
    background 0.2s ease,
    color 0.2s ease,
    filter 0.2s ease;

  ${({ $accentColor, $textColor, $disabled }) => {
    return $disabled
      ? css`
          cursor: default;
          color: ${getColorWithAlpha($textColor, 0.5)};
          background-color: ${getColorWithAlpha($textColor, 0.1)};
        `
      : css`
          cursor: pointer;
          color: ${getInvertedColor($accentColor)};
          background-color: ${$accentColor};

          &:hover {
            filter: brightness(1.2);
          }
        `;
  }}
`;
